import React from 'react';
import { Job } from '../../../data/mockJobs';
import { Building2, MapPin, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

interface JobSelectionProps {
  jobs: Job[];
  selectedJob: string;
  onChange: (jobId: string) => void;
}

const JobSelection = ({ jobs, selectedJob, onChange }: JobSelectionProps) => {
  const { t } = useLanguage();
  const selectedJobDetails = jobs.find(job => job.id === selectedJob);

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return t('candidates.addCandidateModal.jobSelection.priority.high');
      case 'medium':
        return t('candidates.addCandidateModal.jobSelection.priority.medium');
      case 'low':
        return t('candidates.addCandidateModal.jobSelection.priority.low');
      default:
        return priority;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('candidates.addCandidateModal.jobSelection.selectPosition')}
        </label>
        <div className="relative">
          <select
            value={selectedJob}
            onChange={(e) => onChange(e.target.value)}
            className="w-full appearance-none bg-white pl-4 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow"
          >
            <option value="">{t('candidates.addCandidateModal.jobSelection.choosePlaceholder')}</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title} - {job.department}
              </option>
            ))}
          </select>
          <ChevronDown className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {selectedJobDetails && (
        <div className="mt-6 bg-white rounded-xl border-2 border-blue-600 p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900">
              {selectedJobDetails.title}
            </h4>
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                selectedJobDetails.priority === 'high'
                  ? 'bg-red-100 text-red-700'
                  : selectedJobDetails.priority === 'medium'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-green-100 text-green-700'
              }`}
            >
              {getPriorityLabel(selectedJobDetails.priority)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center text-gray-600">
                <Building2 className="w-4 h-4 mr-2" />
                <span className="text-sm">{selectedJobDetails.department}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                <span className="text-sm">{selectedJobDetails.location}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-gray-500">{t('candidates.addCandidateModal.jobSelection.references')}:</span>
                <span className="ml-2 font-medium">
                  {selectedJobDetails.referencesCompleted} / {selectedJobDetails.referencesNeeded}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-gray-500">{t('candidates.addCandidateModal.jobSelection.dueDate')}:</span>
                <span className="ml-2 font-medium">
                  {new Date(selectedJobDetails.dueDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSelection;